import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import MainLayout from '../../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  FileText, 
  Plus, 
  Send, 
  Eye,
  Download,
  Sparkles,
  FileSignature,
  Users,
  Building2,
  Briefcase,
  Search,
  Loader2,
  CheckCircle2,
  Clock,
  AlertCircle,
  PenTool,
  Eraser,
  RefreshCw,
  Server,
  Shield,
  ScrollText,
  Edit2,
  Save
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/axios';
import { cn } from '../../lib/utils';

// =============================================
// SMART CONTRACTS PAGE - AI Generation + E-Signature
// =============================================

const SmartContractsPage = () => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const isAgency = ['agency', 'project_manager', 'customer_service'].includes(user?.role);
  const [contracts, setContracts] = useState([]);
  const [contractTypes, setContractTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [activeTab, setActiveTab] = useState('contracts');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modals
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showSignModal, setShowSignModal] = useState(false);
  const [showCGVModal, setShowCGVModal] = useState(false);
  
  // Selected data
  const [selectedContract, setSelectedContract] = useState(null);
  const [selectedType, setSelectedType] = useState('');
  const [variableFields, setVariableFields] = useState([]);
  const [formData, setFormData] = useState({});
  
  // CGV
  const [activeCGV, setActiveCGV] = useState(null);
  const [generatingCGV, setGeneratingCGV] = useState(false);
  
  // Agency info for pre-filling
  const [agencyInfo, setAgencyInfo] = useState(null);
  
  // Edit mode for contract
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [editedContent, setEditedContent] = useState('');
  
  // Signature canvas
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [signatureData, setSignatureData] = useState({
    signer_name: '',
    signer_first_name: '',
    signer_email: '',
    accepted_terms: false,
    accepted_cgv: false
  });

  useEffect(() => {
    fetchData();
    fetchAgencyInfo();
  }, []);

  const fetchAgencyInfo = async () => {
    try {
      const res = await api.get('/settings/agency-info');
      setAgencyInfo(res.data);
    } catch (error) {
      console.error('Error fetching agency info:', error);
    }
  };

  const fetchData = async () => {
    try {
      const [contractsRes, typesRes, cgvRes] = await Promise.all([
        api.get('/smart-contracts/').catch(() => ({ data: [] })),
        api.get('/smart-contracts/types').catch(() => ({ data: { types: [] } })),
        api.get('/smart-contracts/cgv/active').catch(() => ({ data: null }))
      ]);
      
      setContracts(contractsRes.data || []);
      setContractTypes(typesRes.data?.types || []);
      setActiveCGV(cgvRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load variable fields when contract type changes
  useEffect(() => {
    if (selectedType) {
      loadVariables(selectedType);
    }
  }, [selectedType]);

  const loadVariables = async (type) => {
    try {
      const res = await api.get(`/smart-contracts/variables/${type}`);
      setVariableFields(res.data?.variables || []);
      
      // Pre-fill agency info from settings
      const initial = {};
      (res.data?.variables || []).forEach(v => {
        // Pre-fill agency fields if available
        if (agencyInfo) {
          if (v.key === 'agency_name') initial[v.key] = agencyInfo.agency_name || agencyInfo.name || '';
          else if (v.key === 'agency_address') initial[v.key] = agencyInfo.address || '';
          else if (v.key === 'agency_siret') initial[v.key] = agencyInfo.siret || '';
          else if (v.key === 'agency_email') initial[v.key] = agencyInfo.email || '';
          else if (v.key === 'agency_phone') initial[v.key] = agencyInfo.phone || '';
          else initial[v.key] = '';
        } else {
          initial[v.key] = '';
        }
      });
      setFormData(initial);
    } catch (error) {
      console.error('Error loading variables:', error);
    }
  };

  const handleGenerateContract = async () => {
    // Validate required fields
    const missing = variableFields.filter(v => v.required && !formData[v.key]);
    if (missing.length > 0) {
      toast.error(`Champs requis manquants: ${missing.map(v => v.label).join(', ')}`);
      return;
    }

    setGenerating(true);
    try {
      const res = await api.post('/smart-contracts/generate', {
        contract_type: selectedType,
        variables: formData
      });
      
      toast.success('Contrat généré avec succès !');
      setShowGenerateModal(false);
      fetchData();
      
      // Show preview
      setSelectedContract(res.data);
      setShowPreviewModal(true);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la génération');
    } finally {
      setGenerating(false);
    }
  };

  // Open edit modal for a contract
  const handleEditContract = (contract) => {
    setEditingContract(contract);
    setEditedContent(contract.content_html || '');
    setShowEditModal(true);
  };

  // Save edited contract
  const handleSaveEditedContract = async () => {
    if (!editingContract) return;
    
    try {
      await api.put(`/smart-contracts/${editingContract.id || editingContract.contract_id}`, {
        content_html: editedContent
      });
      
      toast.success('Contrat modifié avec succès !');
      setShowEditModal(false);
      setEditingContract(null);
      fetchData();
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  const handleGenerateCGV = async () => {
    setGeneratingCGV(true);
    try {
      // Get agency info
      const agencyRes = await api.get('/settings/agency-info').catch(() => ({ data: {} }));
      const servicesRes = await api.get('/settings/third-party-services').catch(() => ({ data: [] }));
      
      const agencyInfo = {
        agency_name: agencyRes.data?.agency_name || 'Mon Agence',
        legal_form: agencyRes.data?.legal_form || 'SARL',
        capital: agencyRes.data?.capital || '10000',
        siret: agencyRes.data?.siret || '',
        address: agencyRes.data?.address || '',
        email: agencyRes.data?.email || '',
        phone: agencyRes.data?.phone || '',
        vat_number: agencyRes.data?.vat_number || ''
      };
      
      const services = [
        "Création de sites web",
        "Design graphique et identité visuelle",
        "Production vidéo",
        "Community management",
        "Référencement SEO/SEA",
        "Hébergement web",
        "Maintenance de sites",
        ...(servicesRes.data || []).map(s => s.name)
      ];
      
      const res = await api.post('/smart-contracts/generate-cgv', {
        agency_info: agencyInfo,
        services: [...new Set(services)]
      });
      
      toast.success('CGV générées avec succès !');
      setActiveCGV(res.data);
      setShowCGVModal(false);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la génération des CGV');
    } finally {
      setGeneratingCGV(false);
    }
  };

  // Canvas signature functions
  const initCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#1a365d';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  useEffect(() => {
    if (showSignModal) {
      setTimeout(initCanvas, 100);
    }
  }, [showSignModal, initCanvas]);

  const getMousePos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getMousePos(e);
    
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getMousePos(e);
    
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const handleSignContract = async () => {
    if (!signatureData.signer_name || !signatureData.signer_first_name || !signatureData.signer_email) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    
    if (!signatureData.accepted_terms || !signatureData.accepted_cgv) {
      toast.error('Vous devez accepter les conditions et les CGV');
      return;
    }
    
    const canvas = canvasRef.current;
    const signatureImage = canvas.toDataURL('image/png');
    
    // Check if signature is empty
    const ctx = canvas.getContext('2d');
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const isEmpty = !imageData.data.some((channel, index) => index % 4 !== 3 && channel !== 0);
    
    if (isEmpty) {
      toast.error('Veuillez dessiner votre signature');
      return;
    }

    try {
      const res = await api.post('/smart-contracts/sign', {
        contract_id: selectedContract.contract_id || selectedContract.id,
        signer_name: signatureData.signer_name,
        signer_first_name: signatureData.signer_first_name,
        signer_email: signatureData.signer_email,
        signature_image: signatureImage,
        accepted_terms: signatureData.accepted_terms,
        accepted_cgv: signatureData.accepted_cgv
      });
      
      toast.success(`Contrat signé le ${res.data.timestamp}`);
      setShowSignModal(false);
      fetchData();
      
      // Reset signature data
      setSignatureData({
        signer_name: '',
        signer_first_name: '',
        signer_email: '',
        accepted_terms: false,
        accepted_cgv: false
      });
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la signature');
    }
  };

  const exportToPDF = (content) => {
    // Create a printable version
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Contrat</title>
        <style>
          body { font-family: 'Segoe UI', Arial, sans-serif; padding: 40px; line-height: 1.6; }
          h1 { color: #1a365d; border-bottom: 2px solid #1a365d; padding-bottom: 10px; }
          h2 { color: #2d4a6f; margin-top: 30px; }
          p { margin: 10px 0; }
          ul { margin-left: 20px; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>${content}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { label: 'Brouillon', color: 'bg-slate-100 text-slate-600', icon: Clock },
      sent: { label: 'Envoyé', color: 'bg-blue-100 text-blue-600', icon: Send },
      signed: { label: 'Signé', color: 'bg-emerald-100 text-emerald-600', icon: CheckCircle2 },
      cancelled: { label: 'Annulé', color: 'bg-red-100 text-red-600', icon: AlertCircle }
    };
    const config = statusConfig[status] || statusConfig.draft;
    const Icon = config.icon;
    
    return (
      <Badge className={cn("gap-1", config.color)}>
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  const getTypeIcon = (type) => {
    const icons = {
      freelance: Users,
      affiliate: Briefcase,
      client: Building2,
      maintenance: Server,
      hosting: Shield
    };
    return icons[type] || FileText;
  };

  const getTypeLabel = (type) => {
    const labels = {
      freelance: 'Freelance',
      affiliate: 'Apporteur',
      client: 'Client',
      maintenance: 'Maintenance',
      hosting: 'Hébergement'
    };
    return labels[type] || type;
  };

  const filteredContracts = contracts.filter(c => {
    if (!searchQuery) return true;
    const search = searchQuery.toLowerCase();
    return (
      c.type?.toLowerCase().includes(search) ||
      c.variables?.client_name?.toLowerCase().includes(search) ||
      c.variables?.contractor_name?.toLowerCase().includes(search)
    );
  });

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: theme.colors?.primary }}>
              Contrats Intelligents
            </h1>
            <p className="text-sm text-[#64748b] mt-1">
              Génération IA, signature électronique et CGV dynamiques
            </p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowCGVModal(true)}
              data-testid="manage-cgv-btn"
            >
              <ScrollText className="w-4 h-4 mr-2" />
              Gérer CGV
            </Button>
            <Button 
              onClick={() => setShowGenerateModal(true)} 
              style={{ backgroundColor: theme.colors?.accent }}
              data-testid="generate-contract-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Générer un contrat
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="contracts" data-testid="tab-contracts">
              <FileText className="w-4 h-4 mr-2" />
              Contrats ({contracts.length})
            </TabsTrigger>
            <TabsTrigger value="cgv" data-testid="tab-cgv">
              <ScrollText className="w-4 h-4 mr-2" />
              CGV
            </TabsTrigger>
          </TabsList>

          {/* Contracts Tab */}
          <TabsContent value="contracts" className="space-y-4">
            {/* Search */}
            <div className="relative w-full md:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748b]" />
              <Input 
                placeholder="Rechercher..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-contracts"
              />
            </div>

            {/* Contracts Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredContracts.map((contract) => {
                const TypeIcon = getTypeIcon(contract.type);
                return (
                  <Card key={contract.id} className="card hover:shadow-md transition-shadow">
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${theme.colors?.accent}20` }}>
                            <TypeIcon className="w-5 h-5" style={{ color: theme.colors?.accent }} />
                          </div>
                          <div>
                            <CardTitle className="text-base">
                              {getTypeLabel(contract.type)}
                            </CardTitle>
                            <CardDescription className="text-xs">
                              {contract.variables?.client_name || contract.variables?.contractor_name || 'Sans nom'}
                            </CardDescription>
                          </div>
                        </div>
                        {getStatusBadge(contract.status)}
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="text-xs text-[#64748b] mb-3">
                        Créé le {new Date(contract.created_at).toLocaleDateString('fr-FR')}
                        {contract.signatures?.length > 0 && (
                          <span className="ml-2">
                            • {contract.signatures.length} signature(s)
                          </span>
                        )}
                      </div>
                      
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="flex-1"
                          onClick={() => { setSelectedContract(contract); setShowPreviewModal(true); }}
                          data-testid={`view-contract-${contract.id}`}
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          Voir
                        </Button>
                        {contract.status === 'draft' && (
                          <>
                            <Button 
                              variant="outline"
                              size="sm" 
                              onClick={() => handleEditContract(contract)}
                              data-testid={`edit-contract-${contract.id}`}
                            >
                              <Edit2 className="w-3 h-3" />
                            </Button>
                            <Button 
                              size="sm" 
                              style={{ backgroundColor: theme.colors?.accent }}
                              onClick={() => { setSelectedContract(contract); setShowSignModal(true); }}
                              data-testid={`sign-contract-${contract.id}`}
                            >
                              <FileSignature className="w-3 h-3 mr-1" />
                              Signer
                            </Button>
                          </>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Empty State */}
            {filteredContracts.length === 0 && !loading && (
              <Card className="card p-12 text-center">
                <Sparkles className="w-12 h-12 mx-auto text-[#64748b] mb-4" />
                <h3 className="font-semibold mb-2">Aucun contrat généré</h3>
                <p className="text-sm text-[#64748b] mb-4">
                  Utilisez l&apos;IA pour générer vos contrats professionnels
                </p>
                <Button 
                  onClick={() => setShowGenerateModal(true)} 
                  style={{ backgroundColor: theme.colors?.accent }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Générer mon premier contrat
                </Button>
              </Card>
            )}
          </TabsContent>

          {/* CGV Tab */}
          <TabsContent value="cgv" className="space-y-4">
            {activeCGV?.content_html ? (
              <Card className="card">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Conditions Générales de Vente</CardTitle>
                      <CardDescription>
                        Version du {new Date(activeCGV.created_at).toLocaleDateString('fr-FR')}
                      </CardDescription>
                    </div>
                    <div className="flex gap-2">
                      {/* Only agency can download PDF */}
                      {isAgency && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => exportToPDF(activeCGV.content_html)}
                          data-testid="export-cgv-pdf"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          PDF
                        </Button>
                      )}
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setShowCGVModal(true)}
                        data-testid="regenerate-cgv"
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Régénérer
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div 
                    className="prose prose-sm max-w-none p-4 bg-slate-50 rounded-lg border overflow-y-auto max-h-[600px]"
                    dangerouslySetInnerHTML={{ __html: activeCGV.content_html }}
                  />
                </CardContent>
              </Card>
            ) : (
              <Card className="card p-12 text-center">
                <ScrollText className="w-12 h-12 mx-auto text-[#64748b] mb-4" />
                <h3 className="font-semibold mb-2">Aucune CGV générée</h3>
                <p className="text-sm text-[#64748b] mb-4">
                  Générez vos CGV automatiquement avec l&apos;IA
                </p>
                <Button 
                  onClick={() => setShowCGVModal(true)} 
                  style={{ backgroundColor: theme.colors?.accent }}
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Générer mes CGV
                </Button>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Generate Contract Modal */}
      <Dialog open={showGenerateModal} onOpenChange={setShowGenerateModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5" style={{ color: theme.colors?.accent }} />
              Générer un contrat avec l&apos;IA
            </DialogTitle>
            <DialogDescription>
              Sélectionnez le type de contrat et remplissez les informations
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Contract Type Selection */}
            <div>
              <Label>Type de contrat</Label>
              <Select value={selectedType} onValueChange={setSelectedType}>
                <SelectTrigger data-testid="select-contract-type">
                  <SelectValue placeholder="Choisir un type..." />
                </SelectTrigger>
                <SelectContent>
                  {contractTypes.map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      <div className="flex items-center gap-2">
                        {React.createElement(getTypeIcon(type.id), { className: "w-4 h-4" })}
                        <span>{type.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedType && contractTypes.find(t => t.id === selectedType)?.description && (
                <p className="text-xs text-[#64748b] mt-1">
                  {contractTypes.find(t => t.id === selectedType)?.description}
                </p>
              )}
            </div>

            {/* Dynamic Variables Form */}
            {variableFields.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
                {variableFields.map((field) => (
                  <div key={field.key} className={field.type === 'textarea' ? 'md:col-span-2' : ''}>
                    <Label>
                      {field.label}
                      {field.required && <span className="text-red-500 ml-1">*</span>}
                    </Label>
                    {field.type === 'textarea' ? (
                      <Textarea
                        value={formData[field.key] || ''}
                        onChange={(e) => setFormData({ ...formData, [field.key]: e.target.value })}
                        placeholder={`Entrez ${field.label.toLowerCase()}...`}
                        rows={3}
                        data-testid={`input-${field.key}`}
                      />
                    ) : (
                      <Input
                        type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : field.type === 'email' ? 'email' : 'text'}
                        value={formData[field.key] || ''}
                        onChange={(e) => setFormData({ ...formData, [field.key]: e.target.value })}
                        placeholder={`Entrez ${field.label.toLowerCase()}...`}
                        data-testid={`input-${field.key}`}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGenerateModal(false)}>Annuler</Button>
            <Button 
              onClick={handleGenerateContract} 
              disabled={!selectedType || generating}
              style={{ backgroundColor: theme.colors?.accent }}
              data-testid="generate-contract-submit"
            >
              {generating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Génération en cours...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Générer le contrat
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Modal */}
      <Dialog open={showPreviewModal} onOpenChange={setShowPreviewModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Aperçu du contrat</DialogTitle>
          </DialogHeader>

          {selectedContract && (
            <div className="py-4">
              {/* Status & Info */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  {getStatusBadge(selectedContract.status)}
                  <span className="text-sm text-[#64748b]">
                    {getTypeLabel(selectedContract.type)}
                  </span>
                </div>
                {selectedContract.signatures?.length > 0 && (
                  <Badge variant="outline" className="gap-1">
                    <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                    Signé le {new Date(selectedContract.signed_at).toLocaleDateString('fr-FR')}
                  </Badge>
                )}
              </div>

              {/* Contract Content */}
              <div 
                className="prose prose-sm max-w-none p-6 bg-white rounded-lg border shadow-sm overflow-y-auto max-h-[500px]"
                dangerouslySetInnerHTML={{ __html: selectedContract.content_html }}
              />

              {/* Signatures */}
              {selectedContract.signatures?.length > 0 && (
                <div className="mt-4 p-4 bg-slate-50 rounded-lg border">
                  <h4 className="font-medium mb-2">Signatures</h4>
                  {selectedContract.signatures.map((sig, idx) => (
                    <div key={idx} className="flex items-center gap-4 py-2 border-b last:border-0">
                      <img 
                        src={sig.signature_image} 
                        alt="Signature" 
                        className="w-24 h-12 object-contain border rounded"
                      />
                      <div className="text-sm">
                        <p className="font-medium">{sig.signer_first_name} {sig.signer_name}</p>
                        <p className="text-[#64748b]">{sig.signer_email}</p>
                        <p className="text-xs text-[#94a3b8]">Signé le {sig.timestamp_readable}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreviewModal(false)}>Fermer</Button>
            {/* Only agency can download PDF */}
            {isAgency && (
              <Button 
                variant="outline"
                onClick={() => selectedContract && exportToPDF(selectedContract.content_html)}
              >
                <Download className="w-4 h-4 mr-2" />
                Exporter PDF
              </Button>
            )}
            {selectedContract?.status === 'draft' && (
              <Button 
                style={{ backgroundColor: theme.colors?.accent }}
                onClick={() => { setShowPreviewModal(false); setShowSignModal(true); }}
              >
                <FileSignature className="w-4 h-4 mr-2" />
                Signer ce contrat
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sign Contract Modal */}
      <Dialog open={showSignModal} onOpenChange={setShowSignModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSignature className="w-5 h-5" style={{ color: theme.colors?.accent }} />
              Signature électronique
            </DialogTitle>
            <DialogDescription>
              Signez ce contrat de manière sécurisée avec horodatage
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Signer Info */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Prénom <span className="text-red-500">*</span></Label>
                <Input
                  value={signatureData.signer_first_name}
                  onChange={(e) => setSignatureData({ ...signatureData, signer_first_name: e.target.value })}
                  placeholder="Prénom"
                  data-testid="sign-first-name"
                />
              </div>
              <div>
                <Label>Nom <span className="text-red-500">*</span></Label>
                <Input
                  value={signatureData.signer_name}
                  onChange={(e) => setSignatureData({ ...signatureData, signer_name: e.target.value })}
                  placeholder="Nom"
                  data-testid="sign-last-name"
                />
              </div>
            </div>
            <div>
              <Label>Email <span className="text-red-500">*</span></Label>
              <Input
                type="email"
                value={signatureData.signer_email}
                onChange={(e) => setSignatureData({ ...signatureData, signer_email: e.target.value })}
                placeholder="email@exemple.com"
                data-testid="sign-email"
              />
            </div>

            {/* Signature Canvas */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Votre signature <span className="text-red-500">*</span></Label>
                <Button variant="ghost" size="sm" onClick={clearSignature}>
                  <Eraser className="w-4 h-4 mr-1" />
                  Effacer
                </Button>
              </div>
              <div className="border-2 border-dashed rounded-lg p-1 bg-white">
                <canvas
                  ref={canvasRef}
                  width={400}
                  height={150}
                  className="w-full cursor-crosshair touch-none"
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                  data-testid="signature-canvas"
                />
              </div>
              <p className="text-xs text-[#64748b] mt-1 flex items-center gap-1">
                <PenTool className="w-3 h-3" />
                Dessinez votre signature avec la souris ou le doigt
              </p>
            </div>

            {/* Acceptations */}
            <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="accept-terms"
                  checked={signatureData.accepted_terms}
                  onCheckedChange={(checked) => setSignatureData({ ...signatureData, accepted_terms: checked })}
                  data-testid="accept-terms"
                />
                <label htmlFor="accept-terms" className="text-sm cursor-pointer">
                  Je reconnais avoir lu et approuvé l&apos;intégralité du contrat ci-dessus
                </label>
              </div>
              <div className="flex items-start gap-3">
                <Checkbox
                  id="accept-cgv"
                  checked={signatureData.accepted_cgv}
                  onCheckedChange={(checked) => setSignatureData({ ...signatureData, accepted_cgv: checked })}
                  data-testid="accept-cgv"
                />
                <label htmlFor="accept-cgv" className="text-sm cursor-pointer">
                  J&apos;accepte les Conditions Générales de Vente (CGV) de l&apos;agence
                </label>
              </div>
            </div>

            {/* Legal Notice */}
            <div className="text-xs text-[#64748b] p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="font-medium text-amber-700 mb-1">⚠️ Valeur juridique</p>
              <p>Cette signature électronique a valeur légale conformément au règlement eIDAS et à l&apos;article 1367 du Code civil. Elle sera horodatée et associée à votre identité.</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSignModal(false)}>Annuler</Button>
            <Button 
              onClick={handleSignContract}
              style={{ backgroundColor: theme.colors?.accent }}
              data-testid="submit-signature"
            >
              <FileSignature className="w-4 h-4 mr-2" />
              Signer le contrat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CGV Generation Modal */}
      <Dialog open={showCGVModal} onOpenChange={setShowCGVModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ScrollText className="w-5 h-5" style={{ color: theme.colors?.accent }} />
              Générer les CGV
            </DialogTitle>
            <DialogDescription>
              Les CGV seront générées automatiquement à partir des informations de votre agence
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <div className="p-4 bg-slate-50 rounded-lg space-y-2 text-sm">
              <p className="font-medium">Les CGV incluront :</p>
              <ul className="space-y-1 text-[#64748b]">
                <li>• Objet et champ d&apos;application</li>
                <li>• Devis, commandes et prix</li>
                <li>• Conditions de paiement</li>
                <li>• Propriété intellectuelle</li>
                <li>• Protection des données (RGPD)</li>
                <li>• Responsabilité et garanties</li>
                <li>• Et bien plus...</li>
              </ul>
            </div>

            {activeCGV?.content_html && (
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
                <p className="text-amber-700">
                  ⚠️ Une version des CGV existe déjà. La régénération créera une nouvelle version.
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCGVModal(false)}>Annuler</Button>
            <Button 
              onClick={handleGenerateCGV}
              disabled={generatingCGV}
              style={{ backgroundColor: theme.colors?.accent }}
              data-testid="generate-cgv-submit"
            >
              {generatingCGV ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Génération...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Générer les CGV
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Contract Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="w-5 h-5" style={{ color: theme.colors?.accent }} />
              Modifier le contrat
            </DialogTitle>
            <DialogDescription>
              Modifiez le contenu HTML du contrat généré. Les balises dynamiques seront remplacées lors de l&apos;utilisation.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto py-4">
            {/* Dynamic variables reminder */}
            <div className="mb-4 p-3 bg-slate-50 rounded-lg">
              <Label className="text-sm font-medium mb-2 block">Balises dynamiques disponibles :</Label>
              <div className="flex flex-wrap gap-1.5">
                {['{{agency_name}}', '{{agency_address}}', '{{agency_siret}}', '{{client_name}}', '{{client_company}}', '{{project_name}}', '{{contract_date}}', '{{hourly_rate}}', '{{total_amount}}'].map(tag => (
                  <Badge 
                    key={tag} 
                    variant="outline" 
                    className="text-xs cursor-pointer hover:bg-slate-100"
                    onClick={() => {
                      setEditedContent(prev => prev + ' ' + tag);
                    }}
                  >
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>

            {/* HTML Editor */}
            <Textarea
              value={editedContent}
              onChange={(e) => setEditedContent(e.target.value)}
              placeholder="Contenu HTML du contrat..."
              className="min-h-[400px] font-mono text-sm"
              data-testid="contract-editor"
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>Annuler</Button>
            <Button 
              onClick={handleSaveEditedContract}
              style={{ backgroundColor: theme.colors?.accent }}
              data-testid="save-edited-contract"
            >
              <Save className="w-4 h-4 mr-2" />
              Enregistrer les modifications
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};

export default SmartContractsPage;
